import type { FormInstance } from '@arco-design/web-react/es/Form';

import { SKU_RESOURCE_MODULES_FIELD } from '@/delivery-type-plugin/platform/scene-layout/types';

export type SkuType = 'fixed' | 'custom';

export function normalizeSkuType(skuType: unknown): SkuType | undefined {
  return skuType === 'fixed' || skuType === 'custom' ? skuType : undefined;
}

/**
 * SKU 模块勾选：fixed 为 opt-out，custom 为 opt-in（与 scene-layout resolveModuleDefaults 一致）。
 * 空对象 `{}`：无有效勾选态，视为全开（配合 filter 跳过裁剪）；undefined + custom 仍为 opt-in（未声明则未启用）。
 */
export function isSkuModuleEnabled(moduleKey: string, enabledModules?: Record<string, boolean>, skuType?: SkuType): boolean {
  if (enabledModules && Object.keys(enabledModules).length === 0) {
    return true;
  }
  if (skuType === 'custom') {
    return enabledModules?.[moduleKey] === true;
  }
  if (!enabledModules) return true;
  return enabledModules[moduleKey] !== false;
}

/**
 * 模块是否应参与校验 / 镜像确认。
 * 只看 `skuResourceModules`：显式 `false` 跳过；无勾选态 / 空对象 / 缺 key / `true` 均参与。
 * 与 skuType、resourceEditMode 无关——下单无勾选框时会把展示模块写成 `true`。
 */
export function shouldValidateSkuModule(moduleKey: string, enabledModules?: Record<string, boolean>): boolean {
  if (!enabledModules || Object.keys(enabledModules).length === 0) {
    return true;
  }
  return enabledModules[moduleKey] !== false;
}

/** 从表单 skuResourceModules 判断；供 confirm*ImageOnForm 等统一调用 */
export function shouldValidateSkuModuleOnForm(form: Pick<FormInstance, 'getFieldValue'>, moduleKey: string): boolean {
  const enabledModules = form.getFieldValue(SKU_RESOURCE_MODULES_FIELD) as Record<string, boolean> | undefined;
  return shouldValidateSkuModule(moduleKey, enabledModules);
}

/** @deprecated 使用 shouldValidateSkuModuleOnForm */
export function shouldConfirmImageForSkuModule(form: Pick<FormInstance, 'getFieldValue'>, moduleKey: string): boolean {
  return shouldValidateSkuModuleOnForm(form, moduleKey);
}

export function filterSpecBySkuModules<TSpec>(
  spec: TSpec,
  moduleFieldStrips: Record<string, (spec: TSpec) => TSpec>,
  enabledModules?: Record<string, boolean>,
  skuType?: 'fixed' | 'custom',
): TSpec {
  /** undefined / {}：无勾选态，不过滤 */
  if (enabledModules === undefined || Object.keys(enabledModules).length === 0) {
    return spec;
  }
  let next = spec;
  Object.entries(moduleFieldStrips).forEach(([moduleKey, strip]) => {
    if (!isSkuModuleEnabled(moduleKey, enabledModules, skuType)) {
      next = strip(next);
    }
  });
  return next;
}
